import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { VscError } from 'react-icons/vsc';

import {
  Actions,
  Column,
  ErrorTag,
  Intro,
  OutlineButton,
  PrimaryButton,
  Title,
} from 'components/layout/Auth/AuthForm/styles';
import AuthShell from 'components/layout/Auth/AuthShell';
import ChannelStep, { AuthChannel } from 'components/layout/Auth/ChannelStep';
import TokenStep, { TokenStatus } from 'components/layout/Auth/TokenStep';
import { useCountdown } from 'components/layout/Auth/useCountdown';
import { useAuth } from 'hooks/authHook';
import { useMFAContext } from 'hooks/useMFA';
import ResetPassword from 'newComponents/ResetPassword';
import {
  requestGetAccessToken,
  requestGetUserInfos,
  requestOauth,
} from 'services/authServices';
import { getSigninInfo } from 'store/actions/signin-actions';
import { handleUserId } from 'store/actions/user-actions';

import {
  GENERIC_ERROR_MESSAGE,
  SEND_FAILURE_MESSAGE,
  getChannelLabel,
} from './messages';
import { getUserChannels, sendToken } from './services';

type Passo = 'canal' | 'codigo';

/** Mesma janela usada na recuperação de senha. */
const SEGUNDOS_DO_CODIGO = 60;

/** Reenvios antes de mandar o usuário escolher o canal de novo. */
const MAX_REENVIOS = 3;

/** `authHook` e `useMFA` ainda são JS: tipamos o consumo aqui. */
type AuthContextValue = {
  signIn: (data: {
    loginEmail: string;
    accessToken: string;
    refreshToken: string;
    customerType: string;
  }) => void;
  setUserInfos: (data: Record<string, unknown>) => void;
};

type MFAContextValue = {
  credentials: { login: string; password: string; token: string };
  setCredentials: (credentials: {
    login: string;
    password: string;
    token: string;
  }) => void;
  userId: string;
  attempts: number;
  setAttempts: (n: number) => void;
};

type RequestError = {
  response?: {
    status?: number;
    data?: { code?: string; message?: string };
  };
};

/**
 * Duplo fator de autenticação, na mesma casca e com os mesmos componentes da
 * recuperação de senha.
 *
 * O motor continua o do MFA: o código **não** passa por `/validations` — vai
 * junto de login e senha no `requestOauth`, que conclui a autenticação. Os
 * comportamentos que já existiam foram mantidos: envio por e-mail ou SMS,
 * reenvio com limite, e a recuperação quando o envio falha (tentar de novo ou
 * trocar de canal).
 */
const MFARegister = (): JSX.Element => {
  const [passo, setPasso] = useState<Passo>('canal');
  const [channels, setChannels] = useState<AuthChannel[]>([]);
  const [contactId, setContactId] = useState('');
  const [canalComFalha, setCanalComFalha] = useState<AuthChannel | null>(null);
  const [codigo, setCodigo] = useState('');
  const [status, setStatus] = useState<TokenStatus>('aguardando');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [showModalResetPass, setShowModalResetPass] = useState(false);

  const history = useHistory();
  const dispatch = useDispatch();
  const { signIn, setUserInfos } = useAuth() as AuthContextValue;
  const {
    credentials: { login, password },
    setCredentials,
    userId,
    attempts,
    setAttempts,
  } = useMFAContext() as MFAContextValue;

  const { restante, expirado, restart } = useCountdown(SEGUNDOS_DO_CODIGO);

  useEffect(() => {
    if (!login) {
      history.push('/');
      return;
    }

    const carregarCanais = async (): Promise<void> => {
      try {
        const canais = await getUserChannels(userId);
        setChannels(canais);
        setContactId(canais[0]?.contactId ?? '');
      } catch (error) {
        history.push('/');
      }
    };

    carregarCanais();
  }, [login, userId, history]);

  const canalEscolhido = channels.find((c) => c.contactId === contactId);

  const canalAlternativo = canalComFalha
    ? channels.find((c) => c.contactType !== canalComFalha.contactType)
    : undefined;

  /** "Voltar" na primeira tela abandona o duplo fator e limpa o rastro dele. */
  const sairDoFluxo = useCallback((): void => {
    sessionStorage.removeItem('@UltragazEmpresarial:X-Mfa-Login');
    setAttempts(1);
    setCredentials({ login: '', password: '', token: '' });
    history.push('/');
  }, [setAttempts, setCredentials, history]);

  const enviarPara = useCallback(
    async (canal?: AuthChannel): Promise<void> => {
      if (!canal) return;

      setLoading(true);
      // limpa o que estava na tela antes de pedir outro código: os círculos
      // ficam vazios e o erro só reaparece se este envio também falhar
      setErro(null);
      setCodigo('');
      setStatus('aguardando');
      try {
        await sendToken(userId, canal.contactId);
        setContactId(canal.contactId);
        setCanalComFalha(null);
        restart();
        setPasso('codigo');
      } catch (error) {
        // falhou: a tela oferece tentar de novo ou trocar de canal
        setCanalComFalha(canal);
      }
      setLoading(false);
    },
    [userId, restart]
  );

  const enviarCodigo = useCallback(
    (): Promise<void> => enviarPara(canalEscolhido),
    [enviarPara, canalEscolhido]
  );

  const reenviarCodigo = useCallback(async (): Promise<void> => {
    const proxima = attempts + 1;
    setAttempts(proxima);

    if (proxima >= MAX_REENVIOS) {
      setPasso('canal');
      return;
    }

    await enviarPara(canalEscolhido);
  }, [attempts, setAttempts, enviarPara, canalEscolhido]);

  const handleSelectAddress = useCallback(
    (
      customerSiteCode: string,
      customerSiteId: string,
      customerCode: string,
      customerId: string,
      customerName: string,
      addressLine: string,
      addressNumber: string,
      userCustomerRelationshipId: string,
      document: string
    ) => {
      setLoading(false);
      setUserInfos({
        customerSiteCode,
        customerSiteId,
        customerCode,
        customerId,
        customerName,
        customerAddressLine: `${addressLine}, ${addressNumber}`,
        userCustomerRelationshipId,
        document,
      });
    },
    [setUserInfos]
  );

  const confirmarCodigo = useCallback(async (): Promise<void> => {
    setLoading(true);
    setErro(null);
    toast.dismiss();

    try {
      const accessToken = await requestGetAccessToken();
      const responseOauth = await requestOauth(
        login,
        password,
        codigo,
        accessToken,
        dispatch,
        getSigninInfo
      );

      if (responseOauth) {
        setStatus('validado');
        signIn({
          loginEmail: login,
          accessToken: responseOauth.access_token,
          refreshToken: responseOauth.refresh_token,
          customerType: 'Granel',
        });
        requestGetUserInfos(
          login,
          responseOauth.access_token,
          dispatch,
          handleUserId,
          setUserInfos,
          handleSelectAddress,
          history
        );
        return;
      }
      setStatus('invalido');
    } catch (error) {
      const { response } = error as RequestError;
      const code = response?.data?.code;
      const message = response?.data?.message;
      const httpStatus = response?.status;

      if (code === '401.004') {
        setShowModalResetPass(true);
      } else if (code === '401.005' || code === '401.006') {
        // usuário bloqueado ou credencial inválida: não é problema do código
        setErro(message ?? GENERIC_ERROR_MESSAGE);
      } else if (httpStatus === 401 || httpStatus === 422) {
        // código errado ou expirado — a API responde 422.002 aqui.
        // O layout comunica isso nos próprios círculos, sem toast.
        setStatus('invalido');
      } else {
        toast.error(message ?? GENERIC_ERROR_MESSAGE);
      }
    }

    setLoading(false);
  }, [
    login,
    password,
    codigo,
    dispatch,
    signIn,
    setUserInfos,
    handleSelectAddress,
    history,
  ]);

  const tituloCanal = (
    <>
      Duplo fator de autenticação:
      <br />
      por onde deseja receber o código?
    </>
  );

  const conteudo = ((): React.ReactNode => {
    if (canalComFalha) {
      return (
        <Column gap={24}>
          <Intro>
            <Title>Não conseguimos enviar o código</Title>
          </Intro>
          <ErrorTag role="alert">
            <VscError aria-hidden="true" />
            {SEND_FAILURE_MESSAGE}
          </ErrorTag>
          <Actions>
            <PrimaryButton
              type="button"
              shape="pill"
              fullWidth
              disabled={loading}
              onClick={() => enviarPara(canalComFalha)}
            >
              {`Tentar enviar novamente por ${getChannelLabel(
                canalComFalha.contactType
              )}`}
            </PrimaryButton>
            {canalAlternativo && (
              <OutlineButton
                type="button"
                variant="secondary"
                shape="pill"
                fullWidth
                disabled={loading}
                onClick={() => enviarPara(canalAlternativo)}
              >
                {`Receber o código por ${getChannelLabel(
                  canalAlternativo.contactType
                )}`}
              </OutlineButton>
            )}
            <OutlineButton
              type="button"
              variant="secondary"
              shape="pill"
              fullWidth
              onClick={() => setCanalComFalha(null)}
            >
              Voltar
            </OutlineButton>
          </Actions>
        </Column>
      );
    }

    if (passo === 'codigo') {
      return (
        <TokenStep
          title="Código enviado!"
          subtitle={
            canalEscolhido?.contactType === 'PHONE'
              ? 'Digite o código que você recebeu no celular (token):'
              : 'Digite o código que você recebeu no e-mail (token):'
          }
          codigo={codigo}
          status={status}
          restante={restante}
          expirado={expirado}
          loading={loading}
          onChangeCodigo={(valor) => {
            setCodigo(valor);
            if (status === 'invalido') setStatus('aguardando');
          }}
          onConfirmar={confirmarCodigo}
          onReenviar={reenviarCodigo}
          onBack={() => setPasso('canal')}
          onTrocarCanal={() => setPasso('canal')}
        />
      );
    }

    return (
      <ChannelStep
        title={tituloCanal}
        channels={channels}
        contactId={contactId}
        loading={loading}
        erro={erro}
        submitLabel="Enviar código"
        emptyMessage="Não encontramos um canal disponível para enviar o código. Entre em contato com a Central de Atendimento."
        onChangeContact={setContactId}
        onSubmit={enviarCodigo}
        onBack={sairDoFluxo}
      />
    );
  })();

  return (
    <>
      <AuthShell>{conteudo}</AuthShell>
      <ResetPassword
        setShowModal={setShowModalResetPass}
        showModal={showModalResetPass}
      />
    </>
  );
};

export default MFARegister;
